import { Storage } from '@microsoft/office-js-helpers'
import moment from 'moment'
import makePromise from '../office/make-promise'
import { isDesktopWindows } from '../office/client'

export const MOMENT_DATE_FORMAT = 'YYYY-MM-DD HH:mm:ss Z'

type LocalSession = {
  expiration_date: string | boolean
  access_token: string | boolean
  refresh_token: string | boolean
}

export type ServerSession = LocalSession & {
  environment: {
    melody_api_url: string
    public_api_url: string
    rudderstack_write_key: string
    rudderstack_data_plane_url: string
  }
}
let sessionStorageInstance: Storage<string | boolean>

if (typeof document !== 'undefined') {
  sessionStorageInstance = new Storage('session')
}

export const formatSessionValue = (value: string) => {
  if (value === 'true') {
    return true
  }

  if (value === 'false') {
    return false
  }

  return value
}

export async function getSessionKey(key: string) {
  let sessionData: { [key: string]: string } = {}

  if (
    typeof document === 'undefined' &&
    Office.context.mailbox &&
    Office.context.mailbox.item?.sessionData
  ) {
    sessionData = await makePromise(
      Office.context.mailbox.item?.sessionData.getAllAsync,
      Office.context.mailbox.item?.sessionData
    )()
  }

  const data = sessionStorageInstance?.get(key) || sessionData[key]

  if (data === null || (data === undefined && typeof localStorage !== 'undefined')) {
    return localStorage.getItem(key) || false
  }

  return typeof data === 'boolean' ? data : formatSessionValue(String(data))
}

export async function setAuthData(session: ServerSession) {
  sessionStorageInstance?.set('expirationDate', session.expiration_date)
  sessionStorageInstance?.set('accessToken', session.access_token)
  sessionStorageInstance?.set('refreshToken', session.refresh_token)

  if (Office.context.mailbox && Office.context.mailbox.item?.sessionData && isDesktopWindows()) {
    await makePromise(
      Office.context.mailbox.item?.sessionData.setAsync,
      Office.context.mailbox.item?.sessionData
    )('expirationDate', session.expiration_date.toString())

    await makePromise(
      Office.context.mailbox.item?.sessionData.setAsync,
      Office.context.mailbox.item?.sessionData
    )('accessToken', session.access_token.toString())

    await makePromise(
      Office.context.mailbox.item?.sessionData.setAsync,
      Office.context.mailbox.item?.sessionData
    )('refreshToken', session.refresh_token.toString())
  }
}

let whenAuthDataRefreshed: Promise<ServerSession> | null = null

export async function refreshAuthData(): Promise<ServerSession> {
  if (whenAuthDataRefreshed === null) {
    const data = await getAuthData()
    whenAuthDataRefreshed = getUserSession(data)
  }
  const response = await whenAuthDataRefreshed
  setAuthData(response)
  whenAuthDataRefreshed = null

  return response
}

export async function getAuthData() {
  return {
    expiration_date: await getSessionKey('expirationDate'),
    access_token: await getSessionKey('accessToken'),
    refresh_token: await getSessionKey('refreshToken'),
  }
}

export function removeAuthData() {
  Storage.clearAll()
}

export async function getUserSession(authData: LocalSession): Promise<ServerSession> {
  let url
  if (typeof document === 'undefined') {
    url = JSON.parse(
      // @ts-expect-error we are in the office context
      Office.context.mailbox.officeAppContext?.urls
    )?.javascriptRuntimeUrl?.match(/^(https?:\/\/[^\/]+)/)[1]
  }

  const response = await fetch(
    `${typeof document === 'undefined' ? url : window.location.origin}/auth/current_session.json`,
    {
      credentials: 'include',
      method: 'POST',
      headers: {
        Accept: 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(authData),
    }
  )

  return response.json()
}

function isFutureDate(date: string) {
  return moment.utc(date, MOMENT_DATE_FORMAT).isAfter(moment().utc())
}

export function isSessionValid(
  expiresAt: string | boolean | null,
  accessToken: string | boolean | null
): boolean {
  // we have no proper expiration date or token
  if (typeof expiresAt === 'boolean' || typeof accessToken === 'boolean') {
    return false
  }

  // get_user_session was not dispatched yet
  if (expiresAt === null || accessToken === null) {
    return true
  }

  // expiration in the future
  return isFutureDate(expiresAt)
}
